import { useEffect, useState } from 'react';
import { useFragment } from '@apollo/client';
import { useRouteMatch } from 'react-router-dom';

import { SellingConditionEnum } from '@models';
import { previousFirstPlaceFeatureBannerPriorityKey } from '@constants';
import { productDetails } from '@legacyUrls';
import { Skeleton } from '@ui/Skeleton';
import {
  ProductDetailsFeatureBanner,
  FeatureBannerType,
  FeatureBanner,
} from '@components/ProductDetailsFeatureBanner';
import { useIsAuth } from '@hook/useIsAuth';

import {
  ProductDetailsFeatureBannerFragment,
  ProductDetailsFeatureBannerFragmentDoc,
  useProductDetailsFeatureBannerAuthRequiredQuery,
} from './gql/graphql.v3.generated';

const getPreviousFirstPlaceBannerPriority = () => {
  const storageData = sessionStorage.getItem(previousFirstPlaceFeatureBannerPriorityKey);

  return Number(storageData) || 0;
};

const setPreviousFirstPlaceBannerPriority = (priority: number) => {
  sessionStorage.setItem(previousFirstPlaceFeatureBannerPriorityKey, String(priority));
};

const BANNERS_PRIORITY = {
  [FeatureBannerType.Smart]: 1,
  [FeatureBannerType.SafePayment]: 2,
};

type Product = {
  __typename?: 'ProductDetails',
  id: number,
}

interface Props {
  className?: string,
  product?: Product,
}

export const ProductDetailsFeatureBannerContainer: React.FC<Props> = ({
  className,
  product,
}) => {
  const match = useRouteMatch<{ productId: string }>(productDetails);
  const [banners, setBanners] = useState<FeatureBanner[]>([]);

  const isAuth = useIsAuth();

  const { data: productData, complete: productDataComplete } = useFragment<ProductDetailsFeatureBannerFragment>({
    fragment: ProductDetailsFeatureBannerFragmentDoc,
    from: product || null,
  });

  const {
    data: featureBannerAuthRequiredData,
    loading: featureBannerAuthRequiredLoading,
  } = useProductDetailsFeatureBannerAuthRequiredQuery({
    skip: !Number(match?.params.productId) || !isAuth,
    context: {
      batch: true,
    },
  });

  useEffect(() => {
    if (!featureBannerAuthRequiredLoading && productDataComplete) {
      const isSwap = Boolean(
        productData.sellingCondition === SellingConditionEnum.Exchange,
      );
      const isSmartBannerCanShow = Boolean(
        !isSwap
        && !featureBannerAuthRequiredData?.viewer?.subscriptionInfo?.endAt,
      );
      const isSafePaymentBannerCanShow = productData.withSafePayment;

      const canShowBanners = ([] as FeatureBanner[])
        .concat(isSmartBannerCanShow
          ? [{
            type: FeatureBannerType.Smart,
            data: {
              title: featureBannerAuthRequiredData?.smartInfo.productBannerText,
            },
          }] : [])
        .concat(isSafePaymentBannerCanShow ? [{ type: FeatureBannerType.SafePayment }] : []);

      const previousFirstPlaceBannerPriority = getPreviousFirstPlaceBannerPriority();

      const initialSortedBannersByPriority = canShowBanners.sort(
        (a, b) => BANNERS_PRIORITY[a.type] - BANNERS_PRIORITY[b.type],
      );
      const [
        lowPriorityBanners,
        hightPriorityBanners,
      ] = initialSortedBannersByPriority.reduce<FeatureBanner[][]>(
        (acc, item) => {
          if (BANNERS_PRIORITY[item.type] <= previousFirstPlaceBannerPriority) {
            acc[0].push(item);
            return acc;
          }

          acc[1].push(item);
          return acc;
        },
        [[], []],
      );

      const resultSortedBanners = hightPriorityBanners.concat(lowPriorityBanners);
      const firstPlaceBanner = resultSortedBanners[0];
      const firstPlaceBannerPriority = BANNERS_PRIORITY[firstPlaceBanner?.type];

      setPreviousFirstPlaceBannerPriority(firstPlaceBannerPriority);

      setBanners(resultSortedBanners);
    }
  }, [
    featureBannerAuthRequiredData,
    featureBannerAuthRequiredLoading,
    productData,
    productDataComplete,
  ]);

  if (featureBannerAuthRequiredLoading || !productDataComplete) {
    return (
      <div className={className}>
        <Skeleton
          width="100%"
          height="70px"
        />
      </div>
    );
  }

  if (!banners.length) {
    return null;
  }

  return (
    <ProductDetailsFeatureBanner
      className={className}
      banners={banners}
    />
  );
};
